import React, { type SyntheticEvent, useEffect, useState } from 'react';

import type { DocNode } from '@atlaskit/adf-schema';
import type { ADFEntity } from '@atlaskit/adf-utils/types';
import { ProviderFactory, type Providers } from '@atlaskit/editor-common/provider-factory';
import FileIcon from '@atlaskit/icon/core/file';
import ImageIcon from '@atlaskit/icon/core/image';
import LinkFilledIcon from '@atlaskit/icon/core/migration/link--link-filled';
import VideoIcon from '@atlaskit/icon/core/video';
import LegacyDocumentIcon from '@atlaskit/icon/glyph/document';
import LegacyImageIcon from '@atlaskit/icon/glyph/image';
import LegacyMediaServicesVideoIcon from '@atlaskit/icon/glyph/media-services/video';
import { SmartCardProvider as Provider } from '@atlaskit/link-provider';
import { type FileState, type MediaClient } from '@atlaskit/media-client';
import { useMediaClient } from '@atlaskit/media-client-react';
import { Inline, xcss } from '@atlaskit/primitives';
import { ReactRenderer } from '@atlaskit/renderer';
import { token } from '@atlaskit/tokens';

import { TruncatedText } from '../../common/ui/truncated-text';
import { getEmojiResource } from '../../common/utils/emoji-provider';
import { getMentionProvider } from '../../common/utils/mention-provider';

import {
	getMediaNameFromMediaNode,
	getMediaNodesFromAdfDocument,
	getMediaTypeFromNode,
	MediaType,
	parseADF,
} from './utils';

const mediaGreyTextStyles = xcss({
	color: 'color.text.subtlest',
});

const MediaIconComponent = ({ mediaType }: { mediaType: MediaType }) => {
	const iconColor = token('color.icon.subtle');
	switch (mediaType) {
		case MediaType.Image:
			return (
				<ImageIcon
					color={iconColor}
					LEGACY_primaryColor={iconColor}
					spacing="spacious"
					LEGACY_fallbackIcon={LegacyImageIcon}
					label="Image"
				/>
			);
		case MediaType.Video:
			return (
				<VideoIcon
					color={iconColor}
					LEGACY_primaryColor={iconColor}
					spacing="spacious"
					LEGACY_fallbackIcon={LegacyMediaServicesVideoIcon}
					label="Video"
				/>
			);
		case MediaType.Link:
			return (
				<LinkFilledIcon
					color={iconColor}
					LEGACY_primaryColor={iconColor}
					spacing="spacious"
					label="Link"
				/>
			);
		default:
			return (
				<FileIcon
					color={iconColor}
					LEGACY_primaryColor={iconColor}
					spacing="spacious"
					LEGACY_fallbackIcon={LegacyDocumentIcon}
					label="Attachment"
				/>
			);
	}
};

type AttachmentsSpaceRendererProps = {
	mediaNodes: ADFEntity[];
};

const hydrateMediaNode = async (
	mediaNode: ADFEntity | undefined,
	mediaClient: MediaClient | undefined,
): Promise<ADFEntity | undefined> => {
	if (mediaNode === undefined) {
		return undefined;
	}
	if (!mediaNode.attrs || mediaClient === undefined) {
		return mediaNode;
	}

	let fileState: FileState;
	try {
		const { id, collection: collectionName } = mediaNode.attrs;

		fileState = await mediaClient.file.getCurrentState(id, {
			collectionName,
		});
		if (fileState.status === 'error') {
			return mediaNode;
		}
	} catch (err) {
		return mediaNode;
	}

	const { name, mimeType, size } = fileState;

	const newAttrs = {
		__fileName: name,
		__fileMimeType: mimeType,
		__fileSize: size,
	};

	mediaNode.attrs = { ...mediaNode.attrs, ...newAttrs };

	return mediaNode;
};

const useHydrateMediaNode = (mediaNode: ADFEntity | undefined) => {
	const mediaClient = useMediaClient();

	const [hydratedMediaNode, setHydratedMediaNode] = useState<ADFEntity | undefined>();

	useEffect(() => {
		hydrateMediaNode(mediaNode, mediaClient).then(setHydratedMediaNode);
	}, [mediaNode, mediaClient]);

	return hydratedMediaNode;
};

const AttachmentsSpaceRenderer = ({ mediaNodes }: AttachmentsSpaceRendererProps) => {
	const firstMediaNode = mediaNodes.at(0);

	if (firstMediaNode === undefined) {
		return null;
	}
	const mediaName = getMediaNameFromMediaNode(firstMediaNode);
	const mediaType = getMediaTypeFromNode(firstMediaNode);
	const additionalText = mediaNodes.length > 1 ? ` +${mediaNodes.length - 1} more` : '';

	return (
		<Inline alignBlock="center" xcss={mediaGreyTextStyles}>
			<MediaIconComponent mediaType={mediaType} />
			<TruncatedText clamp={1} text={mediaName + additionalText} />
		</Inline>
	);
};

const AttachmentsSpaceRendererWithMediaNodeHydration = ({
	mediaNodes,
}: AttachmentsSpaceRendererProps) => {
	const hydratedMediaNode = useHydrateMediaNode(mediaNodes.at(0));

	if (hydratedMediaNode === undefined) {
		return null;
	}

	return <AttachmentsSpaceRenderer mediaNodes={mediaNodes} />;
};

type AdfDocumentRendererProps = {
	adf: string;
	userId?: string;
	cloudId?: string;
	attachmentsRenderStyle?:
		| 'inlinePlainTextPlaceholder'
		| 'attachmentsFooter'
		| 'attachmentsFooterWithMediaHydration';
};

const AdfDocumentRenderer = ({
	adf,
	cloudId,
	userId,
	attachmentsRenderStyle = 'inlinePlainTextPlaceholder',
}: AdfDocumentRendererProps) => {
	let dataProviders: Providers & {
		[key: string]: Promise<any> | undefined;
	} = {};

	if (userId) {
		dataProviders.mentionProvider = getMentionProvider(userId);
	}

	dataProviders.emojiProvider = getEmojiResource(cloudId);

	const mediaNodes = getMediaNodesFromAdfDocument(adf);

	// If using attachments space, parsing will replace media nodes from the document
	const adfDocument = parseADF(adf, attachmentsRenderStyle) as DocNode;

	return (
		<Provider>
			<ReactRenderer
				document={adfDocument}
				dataProviders={ProviderFactory.create(dataProviders)}
				eventHandlers={{
					link: {
						onClick(event: SyntheticEvent<HTMLAnchorElement>) {
							// Prevent event from propagating since it collapses the content before navigating
							event.stopPropagation();
						},
					},
				}}
			/>
			{attachmentsRenderStyle === 'attachmentsFooter' && mediaNodes.length > 0 && (
				<AttachmentsSpaceRenderer mediaNodes={mediaNodes} />
			)}
			{attachmentsRenderStyle === 'attachmentsFooterWithMediaHydration' &&
				mediaNodes.length > 0 && (
					<AttachmentsSpaceRendererWithMediaNodeHydration mediaNodes={mediaNodes} />
				)}
		</Provider>
	);
};

export const MemoizedAdfDocumentRenderer = React.memo(AdfDocumentRenderer);
export const MemoizedAttachmentsSpaceRenderer = React.memo(AttachmentsSpaceRenderer);
export const MemoizedAttachmentsSpaceRenderWithMediaNodeHydration = React.memo(
	AttachmentsSpaceRendererWithMediaNodeHydration,
);
